import React from 'react';
import PropTypes from 'prop-types';
import accounting from 'accounting-js';
import {lStorage} from '..';

const { formatMoney, formatNumber, unformat: unformatValue } = accounting;

const propTypes = {
	value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
	isCurrency: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
	renderPlainString: PropTypes.bool,
	isDecimal: PropTypes.bool,
	className: PropTypes.string,
	ignoreDecimalPlaces: PropTypes.bool,
	/** force monetary symbol */
	customMonetarySymbol: PropTypes.string,
	/* elementId is the ID used to identify in the DOM tree */
	elementId: PropTypes.string,
	fullDecimals: PropTypes.bool,
	unformat: PropTypes.bool,
};

const defaultProps = {
	className: '',
	isCurrency: false,
	renderPlainString: false,
	isDecimal: false,
	ignoreDecimalPlaces: false,
	fullDecimals: false,
};

// This is done because the formatMoney doesn't have an option to move the minus sign to the front of the string
// if the value it's negative. To get the expected format, the original value it's tranform into a positive number,
// then it's formatted and finally we append the minus sign manually
const formatCurrency = (value, options) =>
	value >= 0 ? formatMoney(value, options) : `-${formatMoney(-value, options)}`;

/*
 * Will format the number base on the user current currency settings, that they could modify under
 * [workforce/settings -> Advanced Settings]. The currency settings are being save in localStorage. So every time that
 * get updated I update the DB and localStorage. On the other hand, the prop ignoreDecimalPlaces has been added
 * because in the Dashboard it's need to show whole numbers.
 */
export const NumberFormatter = ({
	value,
	isCurrency,
	renderPlainString,
	isDecimal,
	ignoreDecimalPlaces,
	fullDecimals,
	className,
	unformat,
	customMonetarySymbol,
	elementId,
}) => {
	const getStorageUser = () => {
		try {
			return {
				...lStorage.get({ key: 'current-user' }).user,
				...(customMonetarySymbol
					? { monetarySymbol: customMonetarySymbol }
					: {}),
			};
		} catch (e) {
			return {
				monetarySymbol: (customMonetarySymbol) ? customMonetarySymbol : '$',
				position: 'left',
				decimalChar: '',
				thousandsChar: '',
				decimalPlaces: '',
			};
		}
	};

	// For reference on the format look into http://openexchangerates.github.io/accounting.js/
	// Simple `format` string allows control of symbol position (%v = value, %s = symbol):

	const currencySettings = getStorageUser();

	const { monetarySymbol } = currencySettings;
	const position = currencySettings.position || 'left';
	const isCurrencyAtBegining = position === 'left';
	const format = isCurrencyAtBegining ? '%s%v' : '%v%s';
	const decimalChar = currencySettings.decimalChar || '';
	const thousandsChar = decimalChar === '.' ? ',' : '.';
	const decimalPlaces = currencySettings.decimalPlaces || 0;

	if (unformat) {
		return unformatValue(value, decimalChar || '.');
	}

	const numberDecimals = fullDecimals ? +decimalPlaces : 2;
	accounting.settings = {
		currency: {
			symbol: monetarySymbol,
			format,
			decimal: decimalChar, // decimal point separator
			thousand: thousandsChar, // thousands separator
			precision: ignoreDecimalPlaces ? 0 : +decimalPlaces, // decimal places
		},
		number: {
			precision: isDecimal ? numberDecimals : 0,
			thousand: thousandsChar,
			decimal: decimalChar,
		},
	};

	// parsing value to number
	const parsedValue = +decimalPlaces === 0 ? Math.round(+value) : +value;
	const { currency, number } = accounting.settings;

	if (renderPlainString) {
		return isCurrency
			? formatCurrency(parsedValue, currency)
			: formatNumber(parsedValue, number);
	}

	return (
		<span id={elementId} className={className}>
			{isCurrency
				? formatCurrency(parsedValue, currency)
				: formatNumber(parsedValue, number)}
		</span>
	);
};

NumberFormatter.propTypes = propTypes;
NumberFormatter.defaultProps = defaultProps;
